import { useState, useRef, useEffect, useCallback } from 'react';
import { useToast } from '@/shared/hooks/use-toast';
import type { Socket } from 'socket.io-client';

interface UseMediaDevicesProps {
  localVideoRef: React.RefObject<HTMLVideoElement>;
  localStreamRef: React.MutableRefObject<MediaStream | null>;
  socketRef: React.MutableRefObject<Socket | null>;
  peerRef: React.MutableRefObject<RTCPeerConnection | null>;
  /** WebRTC or Mediasoup service - used for replaceVideoTrack on camera switch */
  webrtcServiceRef?: React.MutableRefObject<{ replaceVideoTrack?: (track: MediaStreamTrack) => Promise<void> } | null>;
  isHost: boolean;
  prejoinState: any;
}

export function useMediaDevices({
  localVideoRef,
  localStreamRef,
  socketRef,
  peerRef,
  webrtcServiceRef,
  isHost,
  prejoinState,
}: UseMediaDevicesProps) {
  const { toast } = useToast();
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentVideoDeviceId, setCurrentVideoDeviceId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  // ✅ FIX: Default to 'user' (front camera) for everyone - mobile browsers default to front
  // We'll update this from actual camera settings in the useEffect below
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };
    checkMobile();
  }, []);

  const refreshDevices = async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const vids = all.filter(d => d.kind === 'videoinput');
      
      // For mobile: Only set devices if there are multiple cameras
      // For desktop: Show all available cameras
      if (isMobile) {
        if (vids.length > 1) {
          setVideoDevices(vids);
        } else {
          setVideoDevices([]);
        }
      } else {
        setVideoDevices(vids);
      }
    } catch (e) {
      console.error('Error refreshing devices:', e);
    }
  };

  async function getFrontBackDevices() {
    const all = await navigator.mediaDevices.enumerateDevices();
    const vids = all.filter(d => d.kind === 'videoinput');

    console.log('📷 Available video devices:', vids.map(d => ({
      id: d.deviceId.substring(0, 8),
      label: d.label
    })));

    // Enhanced patterns for better Samsung/Android support
    const backPatterns = [
      /back/i,
      /rear/i,
      /environment/i,
      /camera\s*0/i,        // "camera 0" or "camera0"
      /camera2\s*0/i,       // Samsung specific "camera2 0"
      /facing back/i,
      /^0$/,                // Sometimes just "0"
    ];

    const frontPatterns = [
      /front/i,
      /user/i,
      /face/i,
      /camera\s*1/i,        // "camera 1" or "camera1"
      /camera2\s*1/i,       // Samsung specific "camera2 1"
      /facing front/i,
      /selfie/i,
      /^1$/,                // Sometimes just "1"
    ];

    // Better pattern matching for camera identification
    const findByLabel = (patterns: RegExp[]) => {
      for (const pattern of patterns) {
        const found = vids.find(d => pattern.test(d.label || ''));
        if (found) {
          console.log(`✅ Found camera by pattern ${pattern}:`, found.label);
          return found;
        }
      }
      return null;
    };

    let back = findByLabel(backPatterns);
    let front = findByLabel(frontPatterns);

    // Fallback: If no matches found, assume first is back, second is front
    if (!back && !front && vids.length >= 2) {
      console.warn('⚠️ No camera labels matched, using position fallback');
      back = vids[0];
      front = vids[1];
    } else if (!back && !front && vids.length === 1) {
      // Only one camera, use it as both
      back = vids[0];
      front = vids[0];
    } else if (!back && front) {
      // Found front but not back - use remaining camera
      back = vids.find(v => v.deviceId !== front!.deviceId) || vids[0];
    } else if (back && !front) {
      // Found back but not front - use remaining camera
      front = vids.find(v => v.deviceId !== back!.deviceId) || vids[vids.length - 1];
    }

    console.log('📷 Final camera assignment:', {
      back: back?.label || 'none',
      front: front?.label || 'none',
    });

    return { front, back, vids };
  }

  async function getNextDeviceForMobile(currentId: string | null, facing: 'user' | 'environment') {
    const { front, back, vids } = await getFrontBackDevices();
    
    // If only one camera, return null
    if (vids.length <= 1) return null;
    
    // Toggle between front and back
    if (facing === 'user') {
      return back;
    } else {
      return front;
    }
  }

  async function getNextDeviceForDesktop(currentId: string | null) {
    const { vids } = await getFrontBackDevices();
    if (!vids.length) return null;
    if (vids.length === 1) return null;
    
    const idx = Math.max(0, vids.findIndex(d => d.deviceId === currentId));
    return vids[(idx + 1) % vids.length];
  }

  const switchCamera = async () => {
    try {
      const pc = peerRef.current;
      const ls = localStreamRef.current;
      const oldTrack = ls?.getVideoTracks()?.[0] || null;

      // ——— Instrumentation: log state BEFORE flip ———
      if (oldTrack) {
        try {
          const settings = oldTrack.getSettings?.();
          const constraints = oldTrack.getConstraints?.();
          const caps = typeof oldTrack.getCapabilities === 'function' ? oldTrack.getCapabilities() : {};
          console.log('[CameraFlip] 📷 BEFORE flip — track:', {
            id: oldTrack.id,
            kind: oldTrack.kind,
            enabled: oldTrack.enabled,
            readyState: oldTrack.readyState,
            settings: settings ? { width: settings.width, height: settings.height, frameRate: settings.frameRate, facingMode: settings.facingMode, deviceId: settings.deviceId } : undefined,
            constraints,
            capabilities: caps ? { width: (caps as any).width, height: (caps as any).height, frameRate: (caps as any).frameRate } : undefined,
          });
        } catch (e) {
          console.warn('[CameraFlip] Could not log old track:', e);
        }
      }
      if (pc) {
        const connState = pc.connectionState;
        const iceState = pc.iceConnectionState;
        console.log('[CameraFlip] 📡 BEFORE flip — connection:', { connectionState: connState, iceConnectionState: iceState });
      }

      // 1. Get next device FIRST (before stopping anything)
      let nextDevice: MediaDeviceInfo | null = null;
      if (isMobile) {
        nextDevice = await getNextDeviceForMobile(currentVideoDeviceId, facingMode);
      } else {
        nextDevice = await getNextDeviceForDesktop(currentVideoDeviceId);
      }

      if (!nextDevice) {
        toast({ title: 'Cannot switch camera', description: 'Only one camera detected' });
        return;
      }

      // 2. Try to acquire new camera stream WITHOUT stopping old track first
      // This prevents the gap where the peer connection has a dead video track
      // Use high-quality constraints so post-flip resolution stays >= 720p (no aspectRatio to avoid crop)
      const HIGH_QUALITY_VIDEO = {
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        frameRate: { ideal: 30, max: 60 },
        // Do NOT force aspectRatio — can cause camera cropping on mobile; use CSS contain for display
      };
      let newStream: MediaStream | null = null;
      const wantFacing = isMobile ? (facingMode === 'user' ? 'environment' : 'user') : undefined;
      let needsStopFirst = false;

      const acquireNewStream = async (): Promise<MediaStream> => {
        if (isMobile && wantFacing) {
          try {
            return await navigator.mediaDevices.getUserMedia({
              video: { ...HIGH_QUALITY_VIDEO, facingMode: { exact: wantFacing } },
              audio: false,
            });
          } catch {
            return await navigator.mediaDevices.getUserMedia({
              video: { ...HIGH_QUALITY_VIDEO, facingMode: { ideal: wantFacing } },
              audio: false,
            });
          }
        } else {
          return await navigator.mediaDevices.getUserMedia({
            video: { ...HIGH_QUALITY_VIDEO, deviceId: { exact: nextDevice!.deviceId } },
            audio: false,
          });
        }
      };

      // First attempt: acquire new camera while old one is still running
      try {
        newStream = await acquireNewStream();
        console.log('✅ New camera acquired without stopping old track (seamless switch)');
      } catch (err: any) {
        // On Android, camera might be locked - need to stop old track first
        if (err.name === 'NotReadableError' || err.name === 'NotFoundError') {
          console.warn('⚠️ Camera locked, stopping old track first (Android fallback)...');
          needsStopFirst = true;
        } else {
          throw err;
        }
      }

      // Fallback: Stop old track first, then acquire new one (Android)
      if (needsStopFirst && oldTrack) {
        console.log('🔄 Stopping old track before switch (Android fallback)');
        oldTrack.stop();

        if (ls) {
          ls.removeTrack(oldTrack);
        }

        // Wait for OS to release camera
        await new Promise(resolve => setTimeout(resolve, 300));

        // Retry with backoff
        let attempts = 0;
        const maxAttempts = 3;
        while (!newStream && attempts < maxAttempts) {
          try {
            attempts++;
            newStream = await acquireNewStream();
            console.log(`✅ Camera acquired after stop-first (attempt ${attempts})`);
          } catch (retryErr: any) {
            console.error(`❌ Camera switch attempt ${attempts} failed:`, retryErr.name);
            if (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, attempts * 300));
            } else {
              throw retryErr;
            }
          }
        }
      }

      if (!newStream) {
        throw new Error('Failed to get camera stream');
      }

      const newTrack = newStream.getVideoTracks()[0];
      if (!newTrack) throw new Error('No video track in new stream');

      // ——— Instrumentation: log state AFTER new stream, BEFORE replace ———
      try {
        const settings = newTrack.getSettings?.();
        const constraints = newTrack.getConstraints?.();
        const caps = typeof newTrack.getCapabilities === 'function' ? newTrack.getCapabilities() : {};
        console.log('[CameraFlip] 📷 AFTER new stream — track:', {
          id: newTrack.id,
          settings: settings ? { width: settings.width, height: settings.height, frameRate: settings.frameRate, facingMode: settings.facingMode } : undefined,
          constraints,
          capabilities: caps ? { width: (caps as any).width, height: (caps as any).height, frameRate: (caps as any).frameRate } : undefined,
        });
      } catch (e) {
        console.warn('[CameraFlip] Could not log new track:', e);
      }

      // 3. Replace track in transport FIRST (seamless for remote peer and recording)
      // Mediasoup: producer.replaceTrack; P2P: RTCPeerConnection sender.replaceTrack
      const svc = webrtcServiceRef?.current;
      if (svc && typeof svc.replaceVideoTrack === 'function') {
        await svc.replaceVideoTrack(newTrack);
        console.log('[CameraFlip] ✅ Track replaced in mediasoup/webrtc service');
      } else {
        const sender = pc?.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(newTrack);
          console.log('[CameraFlip] ✅ Track replaced in peer connection');
          // Log encodings to verify they are not reset to low after replaceTrack
          try {
            const params = sender.getParameters();
            if (params?.encodings?.length) {
              console.log('[CameraFlip] 📡 P2P sender encodings after replace:', params.encodings.map((e: any) => ({
                maxBitrate: e.maxBitrate,
                scaleResolutionDownBy: e.scaleResolutionDownBy,
                rid: e.rid,
              })));
            }
          } catch (e) {
            console.warn('[CameraFlip] Could not get sender parameters:', e);
          }
        }
      }
      if (pc) {
        console.log('[CameraFlip] 📡 AFTER replace — connection:', { connectionState: pc.connectionState, iceConnectionState: pc.iceConnectionState });
      }

      // 4. NOW stop the old track (after replacement is done)
      if (!needsStopFirst && oldTrack) {
        oldTrack.stop();
        console.log('🛑 Old track stopped after seamless replacement');
      }

      // 5. Update local stream reference - PRESERVE AUDIO TRACKS
      // Previous code lost audio tracks by creating new stream with video only
      if (ls) {
        // Remove old video track if still present
        const oldVideoTracks = ls.getVideoTracks();
        oldVideoTracks.forEach(t => {
          ls.removeTrack(t);
        });
        // Add new video track to existing stream (keeps audio)
        ls.addTrack(newTrack);
        localStreamRef.current = ls;
        console.log('✅ Updated local stream (preserved audio tracks):', {
          videoTracks: ls.getVideoTracks().length,
          audioTracks: ls.getAudioTracks().length
        });
      } else {
        localStreamRef.current = new MediaStream([newTrack]);
      }

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
        await localVideoRef.current.play().catch(() => {});
      }

      // 6. Update facingMode state AFTER track is replaced (prevents visual glitch)
      try {
        const settings: any = newTrack.getSettings?.() || {};
        if (settings.facingMode === 'user' || settings.facingMode === 'environment') {
          console.log(`✅ Updated facingMode to: ${settings.facingMode}`);
          setFacingMode(settings.facingMode);
        } else if (isMobile) {
          console.log(`✅ Updated facingMode to (fallback): ${wantFacing || 'environment'}`);
          setFacingMode(wantFacing || 'environment');
        }
        if (settings.deviceId) setCurrentVideoDeviceId(settings.deviceId);
      } catch (err) {
        console.error('Failed to update device settings:', err);
      }

      // 7. Clean up extra tracks from newStream
      newStream.getTracks().forEach(t => {
        if (t !== newTrack) {
          try { t.stop(); } catch {}
        }
      });

      refreshDevices();

    } catch (e: any) {
      console.error('❌ Camera switch failed:', e);
      const msg = e?.name ? `${e.name}: ${e.message || 'camera error'}` : (e?.message || String(e));

      if (e.name === 'NotReadableError') {
        toast({
          title: 'Camera Busy',
          description: 'Please wait a moment and try again. Camera may be in use.'
        });
      } else if (e.name === 'NotAllowedError') {
        toast({
          title: 'Permission Denied',
          description: 'Please allow camera access to switch cameras.'
        });
      } else {
        toast({ title: 'Camera switch failed', description: msg });
      }
    }
  };

  // Device change listener
  useEffect(() => {
    const handler = () => refreshDevices();
    try {
      navigator.mediaDevices?.addEventListener?.('devicechange', handler);
    } catch {}
    return () => {
      try { navigator.mediaDevices?.removeEventListener?.('devicechange', handler); } catch {}
    };
  }, [isMobile]);

  // Initialize device info - runs on mount and when stream changes
  useEffect(() => {
    if (localStreamRef.current) {
      try {
        const vt = localStreamRef.current.getVideoTracks()[0];
        const s: any = vt?.getSettings?.() || {};

        console.log('📷 Initializing camera device info:', {
          deviceId: s.deviceId,
          facingMode: s.facingMode,
          label: vt?.label,
        });

        if (s.deviceId) {
          setCurrentVideoDeviceId(s.deviceId);
        }

        // ✅ CRITICAL FIX: Always update facingMode from actual camera settings
        // This ensures facingMode state matches the real camera being used
        if (s.facingMode && (s.facingMode === 'environment' || s.facingMode === 'user')) {
          console.log('✅ Setting facingMode from camera settings:', s.facingMode);
          setFacingMode(s.facingMode);
        } else {
          // Fallback: use device label to infer facing mode
          const label = (vt?.label || '').toLowerCase();
          if (label.includes('back') || label.includes('rear') || label.includes('environment')) {
            console.log('✅ Inferred facingMode from label (back):', 'environment');
            setFacingMode('environment');
          } else if (label.includes('front') || label.includes('user') || label.includes('face')) {
            console.log('✅ Inferred facingMode from label (front):', 'user');
            setFacingMode('user');
          }
        }
      } catch (err) {
        console.error('❌ Failed to initialize camera device info:', err);
      }
      refreshDevices();
    }
  }, [localStreamRef, isMobile]);

  return {
    videoDevices,
    currentVideoDeviceId,
    facingMode,
    isMobile,
    switchCamera,
    refreshDevices,
  };
}